import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { isMaxValidValue, isMinValidValue } from '../../utils';
import { Container, SliderField, SliderInput, SliderTrack, SliderRange } from './range-slider.styles';

const RangeSlider = ({
  minAllowed,
  maxAllowed,
  currentMin,
  currentMax,
  onChange
}) => {
  const [minVal, setMinVal] = useState(currentMin);
  const [maxVal, setMaxVal] = useState(currentMax);
  const minValRef = useRef(null);
  const maxValRef = useRef(null);
  const range = useRef(null);
  const getPercent = useCallback(value => Math.round((value - minAllowed) / (maxAllowed - minAllowed) * 100), [currentMin, currentMax]);

  const isValidMin = value => isMinValidValue(value, currentMax, minAllowed, maxAllowed);

  const isValidMax = value => isMaxValidValue(value, currentMin, minAllowed, maxAllowed);

  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(+maxValRef.current.value);

      if (range.current) {
        range.current.style.left = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, getPercent]);
  useEffect(() => {
    if (minValRef.current) {
      const minPercent = getPercent(+minValRef.current.value);
      const maxPercent = getPercent(maxVal);

      if (range.current) {
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [maxVal, getPercent]);
  useEffect(() => {
    setMinVal(isValidMin(currentMin) ? currentMin : minAllowed);
    setMaxVal(isValidMax(currentMax) ? currentMax : maxAllowed);
  }, [currentMin, currentMax]);

  const handleUpdate = (newMin, newMax) => {
    onChange({
      currentMin: newMin,
      currentMax: newMax
    });
  };

  function handleMinChange(event) {
    const value = Math.min(+event.target.value, maxVal - 1);
    setMinVal(value);
    handleUpdate(value, maxVal);
  }

  function handleMaxChange(event) {
    const value = Math.max(+event.target.value, minVal + 1);
    setMaxVal(value);
    handleUpdate(minVal, value);
  }

  return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(SliderInput, {
    "data-testid": "slider-input-min",
    type: "range",
    min: minAllowed,
    max: maxAllowed,
    value: minVal,
    ref: minValRef,
    step: 0.01,
    overlapIndex: minVal > maxAllowed - 100 ? 4 : 2,
    onChange: handleMinChange
  }), /*#__PURE__*/React.createElement(SliderInput, {
    "data-testid": "slider-input-max",
    type: "range",
    min: minAllowed,
    max: maxAllowed,
    value: maxVal,
    ref: maxValRef,
    step: 0.01,
    onChange: handleMaxChange
  }), /*#__PURE__*/React.createElement(SliderField, null, /*#__PURE__*/React.createElement(SliderTrack, null), /*#__PURE__*/React.createElement(SliderRange, {
    ref: range
  })));
};

process.env.NODE_ENV !== "production" ? RangeSlider.propTypes = {
  currentMin: PropTypes.number.isRequired,
  currentMax: PropTypes.number.isRequired,
  maxAllowed: PropTypes.number.isRequired,
  minAllowed: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired
} : void 0;
export default RangeSlider;